import { Component, Input, Output, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { environment } from '../../../../environments/environment';
import * as moment from 'moment';

@Component({
  selector: 'app-export-buttons',
  templateUrl: './export-buttons.component.html',
  styleUrls: ['./export-buttons.component.scss']
})
export class ExportButtonsComponent {
  @Input() excelUrl: string = '';
  @Input() pdfUrl: string = '';
  @Input() excelFileName?: string;
  @Input() pdfFileName?: string;
  @Input() disabled: boolean = false;
  @Input() buttonSize: 'sm' | 'md' | 'lg' = 'sm';
  @Input() showLabels: boolean = true;
  @Input() excelLabel: string = 'Export Excel';
  @Input() pdfLabel: string = 'Export PDF';
  @Input() excelButtonClass: string = 'btn-success';
  @Input() pdfButtonClass: string = 'btn-danger';
  @Input() excelIcon: string = 'icon-file-text';
  @Input() pdfIcon: string = 'icon-file';
  
  @Output() excelExported = new EventEmitter<void>();
  @Output() pdfExported = new EventEmitter<void>();
  @Output() excelError = new EventEmitter<any>();
  @Output() pdfError = new EventEmitter<any>();
  
  loadingExcel = false;
  loadingPDF = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private notificationService: NotificationService
  ) { }

  /**
   * Export to Excel
   */
  exportToExcel(): void {
    if (!this.excelUrl) {
      this.notificationService.showError('Excel export URL is not configured');
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      this.notificationService.showError('Authentication token not found. Please login again.');
      return;
    }

    if (this.loadingExcel || this.disabled) {
      return; // Prevent multiple clicks
    }

    this.loadingExcel = true;

    // Use full URL if provided, otherwise construct from environment
    const url = this.excelUrl.startsWith('http') 
      ? this.excelUrl 
      : `${environment.apiBaseUrl}${this.excelUrl.startsWith('/') ? this.excelUrl : '/' + this.excelUrl}`;

    this.http.get(url, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    }).subscribe({
      next: (blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        // Generate filename if not provided
        const fileName = this.excelFileName || `export_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
        link.setAttribute('download', fileName);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        this.notificationService.showSuccess('Excel file downloaded successfully!');
        this.loadingExcel = false;
        this.excelExported.emit();
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.notificationService.showError('Failed to export to Excel');
        this.loadingExcel = false;
        this.excelError.emit(error);
      }
    });
  }

  /**
   * Export to PDF
   */
  exportToPdf(): void {
    if (!this.pdfUrl) {
      this.notificationService.showError('PDF export URL is not configured');
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      this.notificationService.showError('Authentication token not found. Please login again.');
      return;
    }

    if (this.loadingPDF || this.disabled) {
      return; // Prevent multiple clicks
    }

    this.loadingPDF = true;

    // Use full URL if provided, otherwise construct from environment
    const url = this.pdfUrl.startsWith('http') 
      ? this.pdfUrl 
      : `${environment.apiBaseUrl}${this.pdfUrl.startsWith('/') ? this.pdfUrl : '/' + this.pdfUrl}`;

    this.http.get(url, {
      responseType: 'text',
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    }).subscribe({
      next: (html) => {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(html);
          printWindow.document.close();
          this.notificationService.showSuccess('PDF opened in new window. Use browser print (Ctrl+P) to save as PDF.');
        } else {
          this.notificationService.showError('Failed to open PDF. Please check your popup blocker settings.');
        }
        this.loadingPDF = false;
        this.pdfExported.emit();
      },
      error: (error) => {
        console.error('Error exporting to PDF:', error);
        this.notificationService.showError('Failed to export to PDF');
        this.loadingPDF = false;
        this.pdfError.emit(error);
      }
    });
  }

  /**
   * Check if any export is in progress
   */
  get isLoading(): boolean {
    return this.loadingExcel || this.loadingPDF;
  }
}

